import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { Button } from 'primereact/button';
import { useContext, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';

import ButtonCustom from '@/components/common/ui/button';
import DialogCustom from '@/components/common/ui/dialog';
import { InputTextCustomForm } from '@/components/common/ui/input/Input';
import { giftTypeFormSchema, GiftTypeFormType } from '@/shared/schema/gift';
import { GiftTypeResponseType } from '@/types/gift';
import { apiRequest } from '@/services';
import { ToastContext } from '@/components/common/CommonToast';
import { toastErrorMessage, toastMessage } from '@/lib/utils';
import Loader from '@/components/common/loader/Loader';
import { HTTP_STATUS } from '@/shared/constants';

type Props = {
  openPopup?: boolean;
  setOpenPopup: (value: boolean) => void;
  giftType: GiftTypeResponseType[];
  refreshDataGiftType: () => void;
};

export const GiftTypeManagementDialog: React.FC<Props> = ({
  openPopup,
  setOpenPopup,
  giftType,
  refreshDataGiftType,
}) => {
  const [t] = useTranslation('');
  const { apiService } = apiRequest();
  const { toast } = useContext(ToastContext);

  const [editingGiftTypeId, setEditingGiftTypeId] = useState<number | null>(
    null,
  );
  const [deletingGiftTypeId, setDeletingGiftTypeId] = useState<number | null>(
    null,
  );
  const [editingValue, setEditingValue] = useState<string>('');
  const [visibleDialogConfirmDelete, setVisibleDialogConfirmDelete] =
    useState(false);
  const [loading, setLoading] = useState(false);
  const [giftTypeData, setGiftTypeData] = useState<GiftTypeResponseType[]>([]);

  const formMethods = useForm<GiftTypeFormType>({
    mode: 'onChange',
    resolver: yupResolver(giftTypeFormSchema),
    shouldFocusError: false,
    defaultValues: {
      gift_type_name: '',
    },
  });

  const {
    handleSubmit: handleSubmitChild,
    reset,
    formState: { errors },
  } = formMethods;

  const onHidePopup = () => {
    setOpenPopup(false);
    setEditingGiftTypeId(null);
    setEditingValue('');
    refreshDataGiftType();
  };

  function handleError(error: any) {
    const { message, errors, status_code } = error;

    if (status_code === HTTP_STATUS.FORM_ERROR) {
      errors.forEach((e: any) => {
        const { field } = e;
        if (field === 'name') {
          toast?.current?.show(
            toastErrorMessage(t('gift.gift_type_dialog.gift_type_name_exist')),
          );
        }
      });
      return;
    }

    const msg = message;
    toast?.current?.show(toastErrorMessage(msg));
  }

  const onSubmit = async (data: { gift_type_name: string }) => {
    setLoading(true);

    const payload = {
      name: data.gift_type_name,
    };

    const { data: giftTypeResponse, error } =
      await apiService.giftTypes.create(payload);
    setLoading(false);

    if (error) {
      handleError(error);
      return;
    }
    if (giftTypeResponse?.data) {
      setGiftTypeData([
        { ...giftTypeResponse.data, accept_delete: true },
        ...giftTypeData,
      ]);
      reset({ gift_type_name: '' });
    }

    toast?.current?.show(
      toastMessage(t('gift.gift_type_dialog.add_gift_type_success')),
    );
  };

  const onEditGiftType = async (id: number, name: string) => {
    setEditingGiftTypeId(id);
    setEditingValue(name);
  };

  const handleEditGiftType = async () => {
    if (!editingGiftTypeId) return;
    setLoading(true);
    const { error } = await apiService.giftTypes.update(editingGiftTypeId, {
      name: editingValue,
    });
    setLoading(false);

    if (error) {
      handleError(error);
      return;
    }

    setGiftTypeData(
      giftTypeData.map((item) => {
        if (item.id === editingGiftTypeId) {
          return {
            ...item,
            name: editingValue,
          };
        }
        return item;
      }),
    );

    setEditingGiftTypeId(null);
    toast?.current?.show(
      toastMessage(t('gift.gift_type_dialog.edit_gift_type_success')),
    );
  };

  const onDeleteGiftType = (id: number) => {
    setVisibleDialogConfirmDelete(!visibleDialogConfirmDelete);
    if (visibleDialogConfirmDelete) return;

    setDeletingGiftTypeId(id);
  };

  const handleDeleteGiftType = async () => {
    setVisibleDialogConfirmDelete(false);
    if (!deletingGiftTypeId) return;
    setLoading(true);
    const { error } = await apiService.giftTypes.delete(
      deletingGiftTypeId as number,
    );
    setLoading(false);

    if (error) {
      handleError(error);
      return;
    }

    setGiftTypeData(
      giftTypeData.filter((item) => item.id !== deletingGiftTypeId),
    );
    toast?.current?.show(
      toastMessage(t('gift.gift_type_dialog.delete_gift_type_success')),
    );
  };

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingValue(e.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  useEffect(() => {
    if (giftType) {
      setGiftTypeData(giftType);
    }
  }, [giftType]);

  return (
    <DialogCustom
      header={t('gift.gift_type_dialog.title')}
      visible={openPopup}
      onHide={onHidePopup}
      className="min-w-[668px] max-w-[668px] dialog-create-group"
      dismissableMask={false}
      blockScroll
      footer={
        <div className="field">
          <div className="flex justify-center gap-[20px]">
            <ButtonCustom
              className="w-full"
              type="button"
              severity="secondary"
              onClick={onHidePopup}
            >
              {t('gift.gift_type_dialog.cancel')}
            </ButtonCustom>
          </div>
        </div>
      }
    >
      <>
        <FormProvider {...formMethods}>
          <form autoComplete="off" onKeyDown={(e) => handleKeyDown(e)}>
            <div className="p-4">
              <div className="label w-56 flex">
                <span>{t('gift.gift_type_dialog.name')}</span>
                <span className="text-error-500">*</span>
              </div>
              <div className="flex justify-between gap-4 mt-2">
                <InputTextCustomForm
                  name="gift_type_name"
                  wrapClass="w-full"
                  placeholder={t('gift.gift_type_dialog.name_placeholder')}
                  preventShowErrorMessage
                />
                <ButtonCustom
                  className="w-[72px]"
                  type="button"
                  onClick={handleSubmitChild(onSubmit)}
                  disabled={loading}
                >
                  {t('gift.gift_type_dialog.add')}
                </ButtonCustom>
              </div>
              {errors?.gift_type_name && (
                <div className="text-error-500 mt-2">
                  {errors?.gift_type_name?.message}
                </div>
              )}
            </div>
          </form>
        </FormProvider>

        {loading && (
          <div className="bg-red-50">
            <Loader isLoading={loading} />
          </div>
        )}
        <div className={cx({ 'opacity-50': loading })}>
          {giftTypeData?.map((item: any, index: number) => (
            <div
              key={index}
              className="p-4 border-t border-gray-200 flex justify-between items-center gap-8"
            >
              <div className="label w-full flex items-center">
                {editingGiftTypeId === item.id ? (
                  <InputTextCustomForm
                    name=""
                    wrapClass="w-full"
                    inputStyle={{
                      border: '0',
                      borderBottom: '1px solid #2E90FA',
                      borderRadius: '0',
                    }}
                    placeholder={t('gift.gift_type_dialog.name_placeholder')}
                    value={editingValue}
                    onChange={onChangeInput}
                  />
                ) : (
                  <span>{`${item.name}`}</span>
                )}
              </div>
              <div className="flex flex-row h-[36px] items-center gap-4">
                {editingGiftTypeId === item.id ? (
                  <div className="flex items-center gap-4">
                    <Button
                      className="p-[5px]"
                      onClick={() => setEditingGiftTypeId(null)}
                    >
                      <span className="pi pi-times" />
                    </Button>
                    <Button
                      className={cx('p-[5px]', {
                        'cursor-not-allowed': !editingValue.length,
                      })}
                      disabled={!editingValue.length}
                      onClick={handleEditGiftType}
                    >
                      <span className="pi pi-check text-primary-700" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    className="p-[5px]"
                    onClick={() => onEditGiftType(item.id, item.name)}
                  >
                    <span className="pi pi-pencil" />
                  </Button>
                )}

                <Button
                  className={cx('p-[5px]', {
                    'cursor-not-allowed ': !item.accept_delete,
                  })}
                  disabled={!item.accept_delete}
                  onClick={() => onDeleteGiftType(item.id)}
                >
                  <span
                    className={cx('pi pi-trash text-error-500', {
                      'text-gray-400': !item.accept_delete,
                    })}
                  />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <DialogCustom
          header={t('gift.gift_type_dialog.gift_type_delete')}
          visible={visibleDialogConfirmDelete}
          onHide={() => setVisibleDialogConfirmDelete(false)}
          headerClassName="border-b border-gray-200 !py-[13px]"
          contentClassName="border-b border-gray-200 !p-[16px] !text-[16px] !leading-[24px] text-center text-gray-900"
          blockScroll
          footer={
            <div className="flex gap-4">
              <ButtonCustom
                type="button"
                className="w-full !rounded-full"
                severity="secondary"
                onClick={() => setVisibleDialogConfirmDelete(false)}
              >
                {t('gift.gift_type_dialog.cancel')}
              </ButtonCustom>
              <ButtonCustom
                type="button"
                className="w-full !rounded-full"
                onClick={handleDeleteGiftType}
              >
                {t('gift.gift_type_dialog.gift_type_delete_yes')}
              </ButtonCustom>
            </div>
          }
        >
          {t('gift.gift_type_dialog.gift_type_delete_confirm')}
        </DialogCustom>
      </>
    </DialogCustom>
  );
};
